import {
    ReceiptTokenView,
    getKctGroupHelp,
    getKctTokenLabel,
    getTokenHelp,
    groupReceiptTokens,
} from "../../helpers/token-descriptions";

interface ReceiptSummaryProps {
    lines: { label: string; value: string }[];
    total: string;
}

const highlightPanel = "rounded-lg border border-engage-200 bg-engage-100";

export function ReceiptSummary({ lines, total }: ReceiptSummaryProps) {
    return (
        <section className={`${highlightPanel} px-4 py-3`}>
            <h2 className="mb-2 text-sm font-bold uppercase tracking-wide text-engage-700">Summary</h2>
            <div className="space-y-1">
                {lines.map((line) => (
                    <ReceiptLine key={line.label} label={line.label} value={line.value} />
                ))}
            </div>
            <div className="mt-2 border-t border-engage-200 pt-2">
                <ReceiptLine label="Total paid" value={total} bold />
            </div>
        </section>
    );
}

interface ReceiptLineProps {
    label: string;
    value: string;
    bold?: boolean;
    highlight?: boolean;
}

export function ReceiptLine({ label, value, bold, highlight }: ReceiptLineProps) {
    return (
        <div className="flex items-baseline justify-between gap-4 py-1.5">
            <span className={`shrink min-w-0 pr-2 ${bold ? "font-bold text-engage-700" : "text-gray-600"}`}>
                {label}
            </span>
            <span
                className={`shrink-0 text-right tabular-nums ${
                    highlight ? "text-lg font-bold text-engage-800" : bold ? "font-bold text-engage-800" : "font-medium text-gray-900"
                }`}
            >
                {value}
            </span>
        </div>
    );
}

interface ReceiptSectionProps {
    title: string;
    children: React.ReactNode;
    subdued?: boolean;
}

export function ReceiptSection({ title, children, subdued }: ReceiptSectionProps) {
    return (
        <section className={`rounded-lg border px-4 py-3 ${subdued ? "border-gray-200 bg-white" : "border-gray-200 bg-gray-50/80"}`}>
            <h2 className="mb-2 text-sm font-bold text-engage-600">{title}</h2>
            {children}
        </section>
    );
}

interface KctTokenGroupDisplayProps {
    tokens: ReceiptTokenView[];
}

export function KctTokenGroupDisplay({ tokens }: KctTokenGroupDisplayProps) {
    const help = getKctGroupHelp(tokens);

    return (
        <div className={`${highlightPanel} px-4 py-4`}>
            <p className="text-center text-sm font-bold text-engage-800">Key change tokens</p>
            <p className="mt-2 text-center text-sm leading-relaxed text-gray-700">{help.description}</p>
            <p className="mt-2 text-center text-xs font-medium text-engage-700">{help.enterHint}</p>

            <div className="mt-4 space-y-3">
                {tokens.map((item, index) => (
                    <div
                        key={`${item.type}-${index}`}
                        className="rounded-lg border border-engage-200 bg-white px-3 py-3 text-center"
                    >
                        <p className="text-xs font-bold uppercase tracking-wide text-engage-700">
                            {getKctTokenLabel(item.type, index)}
                        </p>
                        {item.type.trim().toLowerCase() !== getKctTokenLabel(item.type, index).toLowerCase() && (
                            <p className="mt-0.5 text-xs text-gray-500">{item.type}</p>
                        )}
                        <p className="mt-2 font-mono text-lg sm:text-xl font-bold leading-snug tracking-wide text-gray-900 break-all">
                            {item.token}
                        </p>
                    </div>
                ))}
            </div>
        </div>
    );
}

interface TokenDisplayProps {
    token: ReceiptTokenView;
    hasKctGroup: boolean;
}

export function TokenDisplay({ token, hasKctGroup }: TokenDisplayProps) {
    const showUnits = token.units && parseFloat(token.units) > 0;
    const help = getTokenHelp(token.type, hasKctGroup);

    return (
        <div className={`${highlightPanel} px-4 py-4 text-center`}>
            <p className="text-sm font-bold text-engage-800">{help.title}</p>
            {token.type.trim().toLowerCase() !== help.title.toLowerCase() && (
                <p className="mt-0.5 text-xs text-gray-500">{token.type}</p>
            )}
            <p className="mt-2 text-sm leading-relaxed text-gray-700">{help.description}</p>
            {showUnits && (
                <p className="mt-2 text-base font-semibold text-gray-900">
                    {token.units} {token.unit}
                </p>
            )}
            <p className="mt-3 font-mono text-xl sm:text-2xl font-bold leading-snug tracking-wide text-gray-900 break-all">
                {token.token}
            </p>
            {help.enterHint && (
                <p className="mt-3 text-xs font-medium text-engage-700">{help.enterHint}</p>
            )}
        </div>
    );
}

interface ReceiptTokenListProps {
    tokens: ReceiptTokenView[];
}

export function ReceiptTokenList({ tokens }: ReceiptTokenListProps) {
    const groups = groupReceiptTokens(tokens);
    const hasKctGroup = groups.some((g) => g.kind === "kct");

    return (
        <>
            {groups.map((group, index) =>
                group.kind === "kct" ? (
                    <KctTokenGroupDisplay key={`kct-${index}`} tokens={group.tokens} />
                ) : (
                    <TokenDisplay key={`single-${index}`} token={group.token} hasKctGroup={hasKctGroup} />
                )
            )}
        </>
    );
}
